import type {AmenityConfig} from "./amenity-config";
import type {AmenityType} from "../types";

const HELP_DOMAIN_BY_TYPE: Record<AmenityType, string> = {
  pool: "pool-access-log",
  tennis: "tennis-access-log",
  gym: "gym-access-log",
  recreation: "recreation-access-log",
};

/** Suffix for the facility combobox help target (e.g. `pool-access-log.form.pool`). */
const FACILITY_FIELD_KEY_BY_TYPE: Record<AmenityType, string> = {
  pool: "pool",
  tennis: "court",
  gym: "gym",
  recreation: "area",
};

export type AmenityAccessLogHelp = {
  domain: string;
  form: {
    property: string;
    household: string;
    resident: string;
    facility: string;
    guests: string;
    keyFobNumber: string;
    details: string;
    images: string;
    sendNotification: string;
    submit: string;
  };
  list: {
    search: string;
    filters: string;
    new: string;
    rows: string;
    row: string;
    rowActions: string;
    actionView: string;
    actionEdit: string;
    actionDelete: string;
  };
  tours: {
    listIntro: string;
    formCreate: string;
  };
};

export function getAmenityAccessLogHelpDomain(type: AmenityType): string {
  return HELP_DOMAIN_BY_TYPE[type];
}

export function getAmenityAccessLogHelp(config: AmenityConfig): AmenityAccessLogHelp {
  const domain = HELP_DOMAIN_BY_TYPE[config.type];
  const facilityKey = FACILITY_FIELD_KEY_BY_TYPE[config.type];

  return {
    domain,
    form: {
      property: `${domain}.form.property`,
      household: `${domain}.form.household`,
      resident: `${domain}.form.resident`,
      facility: `${domain}.form.${facilityKey}`,
      guests: `${domain}.form.guests`,
      keyFobNumber: `${domain}.form.key_fob_number`,
      details: `${domain}.form.details`,
      images: `${domain}.form.images`,
      sendNotification: `${domain}.form.send_notification`,
      submit: `${domain}.form.submit`,
    },
    list: {
      search: `${domain}.list.search`,
      filters: `${domain}.list.filters`,
      new: `${domain}.list.new`,
      rows: `${domain}.list.rows`,
      row: `${domain}.list.row`,
      rowActions: `${domain}.list.row_actions`,
      actionView: `${domain}.list.action_view`,
      actionEdit: `${domain}.list.action_edit`,
      actionDelete: `${domain}.list.action_delete`,
    },
    tours: {
      listIntro: `${domain}.list.intro`,
      formCreate: `${domain}.form.create`,
    },
  };
}

/** Static exports for help:extract validation (tennis / gym / recreation). */
export const TENNIS_ACCESS_LOG_FORM_HELP_KEYS = {
  property: "tennis-access-log.form.property",
  household: "tennis-access-log.form.household",
  resident: "tennis-access-log.form.resident",
  court: "tennis-access-log.form.court",
  guests: "tennis-access-log.form.guests",
  keyFobNumber: "tennis-access-log.form.key_fob_number",
  details: "tennis-access-log.form.details",
  images: "tennis-access-log.form.images",
  sendNotification: "tennis-access-log.form.send_notification",
  submit: "tennis-access-log.form.submit",
} as const;

export const TENNIS_ACCESS_LOG_LIST_HELP_KEYS = {
  search: "tennis-access-log.list.search",
  filters: "tennis-access-log.list.filters",
  new: "tennis-access-log.list.new",
  rows: "tennis-access-log.list.rows",
  row: "tennis-access-log.list.row",
  rowActions: "tennis-access-log.list.row_actions",
  actionView: "tennis-access-log.list.action_view",
  actionEdit: "tennis-access-log.list.action_edit",
  actionDelete: "tennis-access-log.list.action_delete",
} as const;

export const GYM_ACCESS_LOG_FORM_HELP_KEYS = {
  property: "gym-access-log.form.property",
  household: "gym-access-log.form.household",
  resident: "gym-access-log.form.resident",
  gym: "gym-access-log.form.gym",
  guests: "gym-access-log.form.guests",
  keyFobNumber: "gym-access-log.form.key_fob_number",
  details: "gym-access-log.form.details",
  images: "gym-access-log.form.images",
  sendNotification: "gym-access-log.form.send_notification",
  submit: "gym-access-log.form.submit",
} as const;

export const GYM_ACCESS_LOG_LIST_HELP_KEYS = {
  search: "gym-access-log.list.search",
  filters: "gym-access-log.list.filters",
  new: "gym-access-log.list.new",
  rows: "gym-access-log.list.rows",
  row: "gym-access-log.list.row",
  rowActions: "gym-access-log.list.row_actions",
  actionView: "gym-access-log.list.action_view",
  actionEdit: "gym-access-log.list.action_edit",
  actionDelete: "gym-access-log.list.action_delete",
} as const;

export const RECREATION_ACCESS_LOG_FORM_HELP_KEYS = {
  property: "recreation-access-log.form.property",
  household: "recreation-access-log.form.household",
  resident: "recreation-access-log.form.resident",
  area: "recreation-access-log.form.area",
  guests: "recreation-access-log.form.guests",
  keyFobNumber: "recreation-access-log.form.key_fob_number",
  details: "recreation-access-log.form.details",
  images: "recreation-access-log.form.images",
  sendNotification: "recreation-access-log.form.send_notification",
  submit: "recreation-access-log.form.submit",
} as const;

export const RECREATION_ACCESS_LOG_LIST_HELP_KEYS = {
  search: "recreation-access-log.list.search",
  filters: "recreation-access-log.list.filters",
  new: "recreation-access-log.list.new",
  rows: "recreation-access-log.list.rows",
  row: "recreation-access-log.list.row",
  rowActions: "recreation-access-log.list.row_actions",
  actionView: "recreation-access-log.list.action_view",
  actionEdit: "recreation-access-log.list.action_edit",
  actionDelete: "recreation-access-log.list.action_delete",
} as const;
